/*
  Safe per-browser storage for view preferences (column visibility, saved views, collapsed groups…).

    import { readStored, writeStored, storageKey } from '@/lib/storage';
    const hidden = readStored(storageKey('table', 'projects', 'columns'), []);
    writeStored(storageKey('table', 'projects', 'columns'), ['team']);

  - Every read and write is wrapped in try/catch: private windows, blocked site data and a full quota
    never break a page — reads fall back to the given default, writes return false.
  - Values are JSON. A stored value that is not valid JSON reads as the fallback.
  - Keys are namespaced with `sara.` so they never collide with the auth token / user keys.
  Only for conveniences that may be lost (they live in this browser only) — never for data.
*/

const PREFIX = 'sara.';

function getStore() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

/** 'table', 'projects', 'columns' → 'sara.table.projects.columns' (empty parts are skipped). */
export function storageKey(...parts) {
  return PREFIX + parts.filter((part) => part !== undefined && part !== null && part !== '').join('.');
}

export function readStored(key, fallback = null) {
  const store = getStore();
  if (!store) return fallback;
  try {
    const raw = store.getItem(key);
    if (raw === null || raw === undefined) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

export function writeStored(key, value) {
  const store = getStore();
  if (!store) return false;
  try {
    if (value === undefined) store.removeItem(key);
    else store.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

export function removeStored(key) {
  return writeStored(key, undefined);
}
